import {
  showLoader,
  GetGenericEntityListingApiType,
  getGenericEntityListingAPI,
  experianFinancialClearance,
  hideLoader,
} from '@app-store'
import { PaysignErrorType } from '../types/common'
import { ResponseData, PatientAddress } from '../types/maxRTE.types'
import { store } from './agent.store'
import { ENTITY_NAME, HTTP_STATUS_CONSTANTS } from './constants'
import { sortByLatest } from './helperFunctions'
import { EXPERIAN_STRINGS } from './strings'
import { RecordType } from './types'
import moment from 'moment'

export const hasEIncomeConsent = (data: ResponseData): boolean => {
  if (!data?.c_patient_consents_patient) return false

  const caseId = store.getState().workflowData.caseData?.id
  const productId = store.getState().workflowData.caseData?.products.id

  const today = new Date()
  return data.c_patient_consents_patient.some((consent) => {
    const consentCaseId = consent.case_id
    if (caseId && consentCaseId !== caseId) return false
    if (productId && consent?.product?.id !== productId) return false

    const typeMatch =
      consent?.consent_type?.name?.toLowerCase() ===
      EXPERIAN_STRINGS.E_INCOME_CONSENT_TYPE
    const revoked = consent?.revoked
    if (!typeMatch || revoked) return false
    const startDate = consent?.start_date ? new Date(consent.start_date) : null
    const endDate = consent?.expiration_date
      ? new Date(consent.expiration_date)
      : null
    // --- check date conditions ---
    if (startDate && endDate) {
      return today >= startDate && today <= endDate && !revoked
    }
    if (startDate && !endDate) {
      return today >= startDate && !revoked
    }
    if (!startDate && endDate) {
      return today <= endDate && !revoked
    }
    // no dates → accept based only on product & type
    return !revoked
  })
}
export const hasValidIncomeDetails = (data: ResponseData): boolean => {
  if (!data?.c_patient_income_details_patient) return false
  const today = new Date()
  return data.c_patient_income_details_patient.some((income) => {
    const startDate = income?.created_at ? new Date(income.created_at) : null
    const endDate = income?.effective_end_date
      ? new Date(income.effective_end_date)
      : null
    // --- check date conditions ---
    if (startDate && endDate) {
      return today >= startDate && today <= endDate
    }
    if (startDate && !endDate) {
      return today >= startDate
    }
    if (!startDate && endDate) {
      return today <= endDate
    }
    // no dates → accept based only on product & type
    return true
  })
}
export const getLatestAddressRecord = (
  records: RecordType[]
): PatientAddress | null => {
  const preferredRecords = records?.filter((r) => r?.is_preferred)
  if (preferredRecords?.length > 0) {
    return preferredRecords?.sort(sortByLatest)?.[0]
  }
  const nonPreferredRecords = records?.filter((r) => !r?.is_preferred)
  if (nonPreferredRecords?.length > 0) {
    return nonPreferredRecords?.sort(sortByLatest)?.[0]
  }
  return null
}
const getPatientMissingFields = (
  apiData: {
    first_name: string
    last_name: string
  },
  patientAddress: PatientAddress | null
): string[] => {
  const missing: string[] = []
  if (!apiData?.first_name) {
    missing.push(EXPERIAN_STRINGS.PATIENT_FIRST_NAME)
  }
  if (!apiData?.last_name) {
    missing.push(EXPERIAN_STRINGS.PATIENT_LAST_NAME)
  }
  if (!patientAddress?.address_line_1) {
    missing.push(EXPERIAN_STRINGS.PATIENT_STREET_ADDRESS)
  }
  if (!patientAddress?.city) {
    missing.push(EXPERIAN_STRINGS.PATIENT_CITY)
  }
  if (!patientAddress?.state?.name) {
    missing.push(EXPERIAN_STRINGS.PATIENT_STATE)
  }
  const zip = patientAddress?.zip_code?.toString().slice(0, 5)
  if (!zip) {
    missing.push(EXPERIAN_STRINGS.PATIENT_ZIP_CODE)
  }
  return missing
}
export const handleExperian = async (
  dataId: number,
  setPaysignErrorPopup: (value: React.SetStateAction<PaysignErrorType>) => void
) => {
  try {
    const caseId = store.getState().workflowData?.caseData?.id
    store.dispatch(showLoader())
    let payload: GetGenericEntityListingApiType = {
      entity_name: ENTITY_NAME.PATIENTS,
      select: [
        ENTITY_NAME.C_PATIENT_ADDRESS,
        ENTITY_NAME.C_PATIENT_CONSENT,
        'first_name',
        'last_name',
        'date_of_birth',
      ],
      filters: [],
      related_entities: {
        c_patient_addresses_c_patients: {
          select: ['address_line_1', 'city', 'state', 'zip_code'],
          criteria: [],
        },
        c_patient_consents_patient: {
          select: [
            'case_id',
            'consent_type',
            'product',
            'expiration_date',
            'start_date',
            'revoked',
          ],
          criteria: [],
        },
      },
    }
    const patientResponse = await getGenericEntityListingAPI(payload, dataId)
    if (!patientResponse) return false
    // check valid e-income details
    payload = {
      entity_name: 'patients',
      select: ['c_patient_income_details_patient'],
      filters: [],
      related_entities: {
        c_patient_income_details_patient: {
          select: ['created_at', 'effective_end_date', 'error_message'],
          criteria: [],
          filters: [
            {
              criteria: [
                {
                  field_name: 'effective_end_date',
                  direction: 'DESC',
                },
                {
                  field_name: 'error_message',
                  operation: 'IS_NULL',
                },
              ],
            },
          ],
        },
      },
    }
    const fetchIncomeDetails = await getGenericEntityListingAPI(payload, dataId)
    const hasEincomeDetails = hasValidIncomeDetails(fetchIncomeDetails)
    if (hasEincomeDetails) {
      setPaysignErrorPopup({
        flag: true,
        heading: EXPERIAN_STRINGS.VALID_EINCOME_DETAILS_EXIST,
        message: EXPERIAN_STRINGS.VALID_EINCOME_DETAILS_EXIST_FOR_PATIENT,
      })
      return false
    }
    // check e-income consent
    const hasConsent = hasEIncomeConsent(patientResponse)
    if (!hasConsent) {
      setPaysignErrorPopup({
        flag: true,
        heading: EXPERIAN_STRINGS.VALID_EINCOME_CONSENT_NOT_AVAILABLE,
        message: EXPERIAN_STRINGS.CANNOT_FETCH_EINCOME_NO_CONSENT,
      })
      return false
    }
    const patientAddress = getLatestAddressRecord(
      patientResponse?.c_patient_addresses_c_patients
    )
    const missingFields = getPatientMissingFields(
      patientResponse,
      patientAddress
    )
    if (!dataId) {
      missingFields.push(EXPERIAN_STRINGS.PATIENT_VISIT_NUMBER)
    }
    if (missingFields.length > 0) {
      setPaysignErrorPopup({
        flag: true,
        heading: EXPERIAN_STRINGS.PATIENT_INFORMATION_MISSING,
        message: EXPERIAN_STRINGS.REQUIRED_PATIENT_INFORMATION_MISSING,
        list: missingFields,
      })
      return false
    }
    const financialClearance = await experianFinancialClearance({
      patientId: dataId,
      fname: patientResponse.first_name,
      lname: patientResponse.last_name,
      addr: first64Bytes(patientAddress?.address_line_1 ?? ''),
      city: patientAddress?.city,
      state: patientAddress?.state?.name?.split(' ')?.[0] ?? '',
      dob: moment(patientResponse.date_of_birth, 'YYYY-MM-DD').format(
        'MM/DD/YYYY'
      ),
      zip: patientAddress?.zip_code,
      ...(caseId ? { caseId } : {}),
    })
    if (
      financialClearance?.data?.statusCode < HTTP_STATUS_CONSTANTS.SUCCESS ||
      financialClearance?.data?.statusCode >
        HTTP_STATUS_CONSTANTS.STATUS_CODE_300
    ) {
      setPaysignErrorPopup({
        flag: true,
        heading: '',
        message:
          financialClearance?.data?.body?.message ??
          financialClearance?.data?.body ??
          '',
      })
      return false
    }
    return true
  } catch (error) {
    setPaysignErrorPopup({
      flag: true,
      heading: '',
      message:
        (error as { response: { data?: { message?: string } } })?.response?.data
          ?.message ?? '',
    })
    return false
  } finally {
    store.dispatch(hideLoader())
  }
}
export const first64Bytes = (input: string): string => {
  const encoder = new TextEncoder()
  const decoder = new TextDecoder('utf-8', { fatal: false })
  const bytes = encoder.encode(input)
  const sliced = bytes.slice(0, 64)
  return decoder.decode(sliced)
}
